// libs
import { useCallback, useEffect, useState } from "react";
import { SESSION_STORAGE_KEYS, Storage, useMountedRef } from "@swiggy-private/core-ui";
import { upiIntentAvailableMethods } from "@payment-client/helpers/PaymentHelper";
import { allUpiIntentAvailableMethods, processAndGetFinalUpiIntentData } from "@payment-client/helpers/UpiHelper";
import { useSessionStorage } from "@swiggy-private/checkout-core";
import { captureError } from "@swiggy-private/core-ui";
import { AllUpiIntentAvailableMethodsResponse } from "@swiggy-private/payment-types";
import { isBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";
import {
    DEFAULT_UPI_INTENT_METHODS,
    SWIGGY_MONEY_UPI_INTENT_METHODS,
    TURBO_CHECKOUT_UPI_INTENT_METHODS,
} from "@payment-client/constants/StorageKeys";
import { UpiIntentContextValue } from "@payment-client/contexts/UpiIntentContext";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

const useUpiMethodsAvailable = (
    methodList: string[],
    isTurboPaymentMethod: boolean,
    paymentResponse: GetPaymentMethodsResponse,
): {
    availableUpiIntentMethods: string[];
    upiIntentMethodFetchInProgress: UpiIntentContextValue["upiIntentMethodFetchInProgress"];
} => {
    const isMountedRef = useMountedRef();

    let availableIntentKey;
    if (isBusinessLineSwiggyMoney()) {
        availableIntentKey = SWIGGY_MONEY_UPI_INTENT_METHODS;
    } else if (isTurboPaymentMethod) {
        /* We need to have different cache key for turbo checkout intent methods,
         * since GPO response has a different variant in cart and causes issues on payment page if kept same */
        availableIntentKey = TURBO_CHECKOUT_UPI_INTENT_METHODS;
    } else {
        availableIntentKey = DEFAULT_UPI_INTENT_METHODS;
    }
    const [state, setState] = useSessionStorage<{ availableMethods: string[] } | null>(availableIntentKey, null);
    const [availableCallInProgress, setAvailableCallInProgress] = useState<
        UpiIntentContextValue["upiIntentMethodFetchInProgress"]
    >("notInitialted");

    // this will handle the new NB which sends all the upi apps details from users device
    const handleAllUpiIntentAvailableMethods = useCallback(() => {
        allUpiIntentAvailableMethods(methodList)
            .catch(err => {
                captureError(err);
                return [];
            })
            .then(availableMethods => {
                if (isMountedRef.current) {
                    // TODO: persist below to context to avaoid recomputation
                    const upiIntentData = processAndGetFinalUpiIntentData(
                        paymentResponse,
                        availableMethods as AllUpiIntentAvailableMethodsResponse,
                    );

                    Storage.setItem(
                        SESSION_STORAGE_KEYS.THIRD_PARTY_UPI_INTENT_APPS_DETAILS,
                        upiIntentData.thirdPartyUpiIntentAppsDetails,
                    );

                    setState({
                        availableMethods: upiIntentData.sortedUpiApps,
                    });
                    setAvailableCallInProgress(false);
                }
            });
    }, [isMountedRef, methodList, paymentResponse, setState]);

    /* this will handle the old NB which sends the upi apps details from users 
        device depending on the payment response */
    const handleLegacyUpiIntentAvailableMethods = useCallback(() => {
        upiIntentAvailableMethods(methodList)
            .catch(err => {
                captureError(err);
                return [];
            })
            .then(availableMethods => {
                if (isMountedRef.current) {
                    setState({ availableMethods });
                    setAvailableCallInProgress(false);
                }
            });
    }, [isMountedRef, methodList, setState]);

    useEffect(() => {
        if (state != null && availableCallInProgress === "notInitialted") {
            setAvailableCallInProgress(false);
        }

        if (state != null || availableCallInProgress === true) {
            return;
        }
        setAvailableCallInProgress(true);

        if (paymentResponse?.shouldShowThirdPartyUpiIntentApps) {
            handleAllUpiIntentAvailableMethods();
        } else {
            handleLegacyUpiIntentAvailableMethods();
        }
    }, [
        state,
        availableCallInProgress,
        handleAllUpiIntentAvailableMethods,
        handleLegacyUpiIntentAvailableMethods,
        paymentResponse?.shouldShowThirdPartyUpiIntentApps,
    ]);

    return {
        availableUpiIntentMethods: state?.availableMethods ?? [],
        upiIntentMethodFetchInProgress: availableCallInProgress,
    };
};

export default useUpiMethodsAvailable;
